import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './OrdersTable.css';
import OrderDetailsPanel from './OrderDetailsPanel';
import Pagination from './Pagination';
import { fetchOrderById, fetchTradersData, subscribeToOrderEvents } from '../services/dataApi';
import useUrlDetails from '../hooks/useUrlDetails';

const normalizeTimestamp = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric < 1000000000000 ? numeric * 1000 : numeric;
  }
  return new Date(value).getTime();
};

const matchesDateRange = (order, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  const orderData = order?.data || order || {};
  const created = orderData.created || orderData.Created || orderData.create_time || order?.create_time;
  const createdAt = normalizeTimestamp(created);
  const fromTime = dateFrom ? normalizeTimestamp(dateFrom) : null;
  const toTime = dateTo ? normalizeTimestamp(dateTo) : null;
  return Number.isFinite(createdAt)
    && (!Number.isFinite(fromTime) || createdAt >= fromTime)
    && (!Number.isFinite(toTime) || createdAt <= toTime);
};

const fetchOrderUntilStatus = async (pageType, orderId, expectedStatus) => {
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const freshOrder = await fetchOrderById(pageType, orderId);
    const freshData = freshOrder?.data || freshOrder;
    const freshStatus = String(freshData?.Status || freshData?.status || '').toLowerCase();

    if (freshOrder && freshStatus === expectedStatus) {
      return freshOrder;
    }

    await new Promise((resolve) => window.setTimeout(resolve, 1000));
  }

  return null;
};

const OrdersTable = ({
  data,
  pageType,
  statusFilter = 'all',
  dateFrom = '',
  dateTo = '',
  searchTerm = '',
  filters = {},
  showTraderName = false,
  pagination,
  onPageChange,
  onRefreshNeeded,
  onTotalChange,
  linkedOrder = null
}) => {
  const [copiedId, setCopiedId] = useState(null);
  const [traders, setTraders] = useState([]);
  const [tradersLoading, setTradersLoading] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [orderUpdates, setOrderUpdates] = useState({});
  const [createdOrders, setCreatedOrders] = useState([]);
  const [navigatedOrder, setNavigatedOrder] = useState(null);
  const [rowHighlights, setRowHighlights] = useState({});
  const [isTableHeaderStuck, setIsTableHeaderStuck] = useState(false);
  const checksInFlight = useRef(new Set());
  const nextCheckAt = useRef(new Map());
  const createdOrdersInFlight = useRef(new Set());
  const liveInsertedOrderIds = useRef(new Set());
  const latestLiveStatus = useRef(new Map());
  const refreshTimer = useRef(null);
  const onRefreshNeededRef = useRef(onRefreshNeeded);
  const onTotalChangeRef = useRef(onTotalChange);
  const rowHighlightTimers = useRef(new Map());
  const rowHighlightVariants = useRef(new Map());
  const tableWrapperRef = useRef(null);

  useEffect(() => {
    const updateStickyState = () => {
      if (window.innerWidth < 769 || !tableWrapperRef.current) {
        setIsTableHeaderStuck(false);
        return;
      }

      const bounds = tableWrapperRef.current.getBoundingClientRect();
      const nextState = bounds.top <= 56 && bounds.bottom > 112;
      setIsTableHeaderStuck((current) => current === nextState ? current : nextState);
    };

    updateStickyState();
    window.addEventListener('scroll', updateStickyState, { passive: true });
    window.addEventListener('resize', updateStickyState);
    return () => {
      window.removeEventListener('scroll', updateStickyState);
      window.removeEventListener('resize', updateStickyState);
    };
  }, []);

  useEffect(() => {
    onRefreshNeededRef.current = onRefreshNeeded;
  }, [onRefreshNeeded]);

  useEffect(() => {
    onTotalChangeRef.current = onTotalChange;
  }, [onTotalChange]);

  const scheduleFallbackRefresh = useCallback(() => {
    if (!onRefreshNeededRef.current) return;
    if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    refreshTimer.current = window.setTimeout(() => {
      refreshTimer.current = null;
      onRefreshNeededRef.current?.();
    }, 300);
  }, []);

  const highlightRow = useCallback((orderId, status) => {
    const key = String(orderId || '');
    if (!key) return;
    const normalizedStatus = String(status || '').toLowerCase();
    const colorStatus = normalizedStatus === 'canceled' ? 'cancelled' : normalizedStatus;
    const highlightStatus = ['pending', 'completed', 'cancelled'].includes(colorStatus)
      ? colorStatus
      : 'neutral';

    const previousVariant = rowHighlightVariants.current.get(key) || 'b';
    const variant = previousVariant === 'a' ? 'b' : 'a';
    rowHighlightVariants.current.set(key, variant);

    const previousTimer = rowHighlightTimers.current.get(key);
    if (previousTimer) window.clearTimeout(previousTimer);

    setRowHighlights((current) => ({
      ...current,
      [key]: { status: highlightStatus, variant }
    }));

    const timerId = window.setTimeout(() => {
      setRowHighlights((current) => {
        if (!current[key] || current[key].variant !== variant) return current;
        const next = { ...current };
        delete next[key];
        return next;
      });
      rowHighlightTimers.current.delete(key);
    }, 3200);
    rowHighlightTimers.current.set(key, timerId);
  }, []);

  useEffect(() => () => {
    if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    rowHighlightTimers.current.forEach((timerId) => window.clearTimeout(timerId));
    rowHighlightTimers.current.clear();
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (showTraderName !== true) {
      return;
    }

    const loadTraders = async () => {
      setTradersLoading(true);
      try {
        const tradersData = await fetchTradersData(1, 1000);
        let tradersList = [];

        if (tradersData && tradersData.data && Array.isArray(tradersData.data)) {
          tradersList = tradersData.data;
        } else if (Array.isArray(tradersData)) {
          tradersList = tradersData;
        } else if (tradersData && tradersData.ops && tradersData.ops.length > 0) {
          const firstOp = tradersData.ops[0];
          if (firstOp.list && Array.isArray(firstOp.list)) {
            tradersList = firstOp.list;
          } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
            tradersList = firstOp.data.list;
          }
        }

        setTraders(tradersList);
      } catch (err) {
        console.error('Error loading traders:', err);
        setTraders([]);
      } finally {
        setTradersLoading(false);
      }
    };

    loadTraders();
  }, [showTraderName]);

  const getTraderNameById = (traderId) => {
    if (!traderId) {
      return '-';
    }

    const trader = traders.find((item) => {
      const id = item.traderId || item.TraderId || item.TRADER_ID || item.ref;
      return id === traderId;
    });

    if (!trader) {
      return traderId;
    }

    return trader.name || trader.Name || trader.NAME || trader.login || trader.Login || traderId;
  };

  const copyToClipboard = async (text, orderId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(orderId);
      setTimeout(() => setCopiedId(null), 1600);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const sourceOrders = useMemo(() => {
    if (data && data.data && Array.isArray(data.data)) {
      return data.data;
    }
    if (Array.isArray(data)) {
      return data;
    }
    if (data && data.ops && data.ops.length > 0) {
      const firstOp = data.ops[0];
      if (firstOp.list && Array.isArray(firstOp.list)) {
        return firstOp.list;
      }
      if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
        return firstOp.data.list;
      }
    }
    return [];
  }, [data]);

  useEffect(() => {
    // A completed page reload is authoritative for the active search and filters.
    setCreatedOrders([]);
    setOrderUpdates({});
    liveInsertedOrderIds.current.clear();
  }, [data]);

  const visibleSourceOrders = useMemo(() => {
    const createdOrderIds = new Set(createdOrders.map((createdOrder) => {
      const createdData = createdOrder.data || createdOrder;
      return String(createdData.OrderId || createdData.orderId || createdData.order_id || '');
    }));

    const mergedOrders = [
      ...createdOrders,
      ...sourceOrders.filter((order) => {
        const orderData = order.data || order;
        const orderId = orderData.OrderId || orderData.orderId || orderData.order_id;
        return !createdOrderIds.has(String(orderId || ''));
      })
    ];

    return pagination?.page === 1 && pagination?.limit
      ? mergedOrders.slice(0, pagination.limit)
      : mergedOrders;
  }, [createdOrders, sourceOrders, pagination?.page, pagination?.limit]);

  const hasActiveServerFilters = useMemo(() => Object.values(filters || {}).some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== '' && value !== false;
  }), [filters]);

  const orders = visibleSourceOrders
    .map((order) => {
      const orderData = order.data || order;
      const orderId = orderData.OrderId || orderData.orderId || orderData.order_id;
      return orderId && orderUpdates[String(orderId)]
        ? orderUpdates[String(orderId)]
        : order;
    })
    .filter((order) => {
      if (statusFilter === 'all') {
        return true;
      }
      const orderData = order.data || order;
      const status = String(orderData.Status || orderData.status || '').toLowerCase();
      return statusFilter === 'cancelled'
        ? status === 'cancelled' || status === 'canceled'
        : status === statusFilter;
    })
    .filter((order) => {
      return matchesDateRange(order, dateFrom, dateTo);
    });

  const liveViewRef = useRef(null);
  liveViewRef.current = {
    visibleSourceOrders,
    statusFilter,
    page: pagination?.page || 1,
    searchTerm,
    hasActiveServerFilters,
    dateFrom,
    dateTo
  };

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') scheduleFallbackRefresh();
    };
    const intervalId = window.setInterval(refreshWhenVisible, 60000);

    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('online', scheduleFallbackRefresh);
    window.addEventListener('focus', scheduleFallbackRefresh);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('online', scheduleFallbackRefresh);
      window.removeEventListener('focus', scheduleFallbackRefresh);
    };
  }, [scheduleFallbackRefresh]);

  useEffect(() => subscribeToOrderEvents((event) => {
    const currentView = liveViewRef.current;
    if (
      !['order_status_changed', 'order_created'].includes(event.type)
      || (event.pageType && event.pageType !== pageType)
      || (
        event.type === 'order_created'
        && currentView.statusFilter !== 'all'
        && event.status !== currentView.statusFilter
      )
    ) {
      return;
    }

    const key = String(event.orderId || '');
    const previousLiveStatus = latestLiveStatus.current.get(key);
    latestLiveStatus.current.set(key, event.status);
    const sourceOrder = currentView.visibleSourceOrders.find((order) => {
      const orderData = order.data || order;
      const orderId = orderData.OrderId || orderData.orderId || orderData.order_id;
      return String(orderId || '') === key;
    });

    if (
      event.type === 'order_created'
      && !sourceOrder
      && !createdOrdersInFlight.current.has(key)
      && !liveInsertedOrderIds.current.has(key)
    ) {
      // Newly created orders belong at the top of the first page. Updating any
      // other page would change its composition and break pagination order.
      createdOrdersInFlight.current.add(key);

      const insertCreatedOrder = (freshOrder) => {
        const latestView = liveViewRef.current;
        const alreadyVisible = latestView.visibleSourceOrders.some((order) => {
          const orderData = order.data || order;
          return String(orderData.OrderId || orderData.orderId || orderData.order_id || '') === key;
        });
        if (alreadyVisible) return;

        const stillMatchesStatus = latestView.statusFilter === 'all'
          || event.status === latestView.statusFilter;
        const canInsertDirectly = latestView.page === 1
          && !String(latestView.searchTerm || '').trim()
          && !latestView.hasActiveServerFilters
          && stillMatchesStatus
          && matchesDateRange(freshOrder, latestView.dateFrom, latestView.dateTo);

        if (canInsertDirectly) {
          liveInsertedOrderIds.current.add(key);
          setCreatedOrders((current) => [freshOrder, ...current]);
          highlightRow(key, event.status);
          onTotalChangeRef.current?.(1);
        } else {
          // Search, advanced filters and later pages are server-authoritative.
          // Refresh them silently because a single row cannot be placed safely.
          scheduleFallbackRefresh();
        }
      };

      if (event.order) {
        insertCreatedOrder(event.order);
        createdOrdersInFlight.current.delete(key);
        return;
      }

      const loadCreatedOrder = async () => {
        for (let attempt = 0; attempt < 5; attempt += 1) {
          const freshOrder = await fetchOrderById(pageType, key);
          if (freshOrder) {
            insertCreatedOrder(freshOrder);
            return;
          }
          await new Promise((resolve) => window.setTimeout(resolve, 2000));
        }
      };

      loadCreatedOrder()
        .catch((error) => {
          console.error(`Failed to load created order ${key}:`, error);
        })
        .finally(() => {
          createdOrdersInFlight.current.delete(key);
        });
      return;
    }

    if (!sourceOrder) {
      if (
        event.type === 'order_status_changed'
        && (
          String(currentView.searchTerm || '').trim()
          || currentView.hasActiveServerFilters
          || currentView.statusFilter !== 'all'
        )
      ) {
        scheduleFallbackRefresh();
      }
      return;
    }

    setOrderUpdates((current) => {
      const existing = current[key] || sourceOrder;
      if (existing.data) {
        return {
          ...current,
          [key]: {
            ...existing,
            data: {
              ...existing.data,
              Status: event.status
            }
          }
        };
      }

      return {
        ...current,
        [key]: {
          ...existing,
          Status: event.status
        }
      };
    });
    highlightRow(key, event.status);

    nextCheckAt.current.delete(key);

    if (
      previousLiveStatus !== event.status
      && currentView.statusFilter !== 'all'
      && event.status !== currentView.statusFilter
    ) {
      onTotalChangeRef.current?.(-1);
    }

    if (
      String(currentView.searchTerm || '').trim()
      || currentView.hasActiveServerFilters
      || (currentView.statusFilter !== 'all' && event.status !== currentView.statusFilter)
    ) {
      scheduleFallbackRefresh();
    }

    // The event only contains the new status. Refresh this order alone so
    // completion timestamps and other fields can catch up without reloading
    // and shifting the whole table.
    fetchOrderUntilStatus(pageType, key, event.status)
      .then((freshOrder) => {
        if (!freshOrder || latestLiveStatus.current.get(key) !== event.status) return;
        setOrderUpdates((current) => ({
          ...current,
          [key]: freshOrder
        }));
      })
      .catch((error) => {
        console.error(`Failed to refresh updated order ${key}:`, error);
      });
  }, { onReconnect: scheduleFallbackRefresh }), [pageType, scheduleFallbackRefresh, highlightRow]);

  useEffect(() => {
    const checkedAt = Date.now();
    visibleSourceOrders.forEach((order) => {
      const orderData = order.data || order;
      const orderId = orderData.OrderId || orderData.orderId || orderData.order_id;
      const updatedOrder = orderId ? orderUpdates[String(orderId)] : null;
      const effectiveData = updatedOrder?.data || updatedOrder || orderData;
      const status = String(effectiveData.Status || effectiveData.status || '').toLowerCase();
      const created = effectiveData.created || effectiveData.Created || effectiveData.create_time
        || updatedOrder?.create_time || order.create_time || null;
      const timeout = effectiveData.timeout ?? effectiveData.Timeout ?? effectiveData.TIMEOUT
        ?? updatedOrder?.timeout ?? order.timeout ?? order.Timeout ?? order.TIMEOUT;
      const timeoutSeconds = timeout === null || timeout === undefined || timeout === ''
        ? NaN : Number(timeout);
      const createdAt = normalizeTimestamp(created);
      const expiresAt = createdAt + timeoutSeconds * 1000;
      const key = String(orderId || '');

      if (
        !key
        || status !== 'pending'
        || !Number.isFinite(timeoutSeconds)
        || timeoutSeconds < 0
        || !Number.isFinite(expiresAt)
        || checkedAt < expiresAt
        || checksInFlight.current.has(key)
        || checkedAt < (nextCheckAt.current.get(key) || 0)
      ) {
        return;
      }

      checksInFlight.current.add(key);
      const liveStatusAtRequest = latestLiveStatus.current.get(key);

      fetchOrderById(pageType, key)
        .then((freshOrder) => {
          if (!freshOrder) {
            return;
          }

          // A live event received during this request is newer than its snapshot.
          if (latestLiveStatus.current.get(key) !== liveStatusAtRequest) return;

          setOrderUpdates((current) => ({
            ...current,
            [key]: freshOrder
          }));

          const freshData = freshOrder.data || freshOrder;
          const freshStatus = String(freshData.Status || freshData.status || '').toLowerCase();
          if (freshStatus !== 'pending') {
            latestLiveStatus.current.set(key, freshStatus);
            nextCheckAt.current.delete(key);
            highlightRow(key, freshStatus);
            if (statusFilter !== 'all') {
              onTotalChangeRef.current?.(-1);
              scheduleFallbackRefresh();
            }
          }
        })
        .catch((error) => {
          console.error(`Failed to refresh order ${key}:`, error);
        })
        .finally(() => {
          checksInFlight.current.delete(key);
          // Retry while Corezoid still reports Pending, without overlapping requests.
          nextCheckAt.current.set(key, Date.now() + 2000);
        });
    });
  }, [now, pageType, visibleSourceOrders, orderUpdates, statusFilter, scheduleFallbackRefresh, highlightRow]);

  const linkedOrderData = linkedOrder?.data || linkedOrder;
  const linkedOrderId = linkedOrderData
    ? linkedOrderData.OrderId || linkedOrderData.orderId || linkedOrderData.order_id
    : null;
  const effectiveLinkedOrder = linkedOrderId && orderUpdates[String(linkedOrderId)]
    ? orderUpdates[String(linkedOrderId)]
    : linkedOrder;
  const detailRecords = [
    ...orders,
    ...(effectiveLinkedOrder ? [effectiveLinkedOrder] : []),
    ...(navigatedOrder ? [navigatedOrder] : [])
  ];
  const [selectedOrder, setSelectedOrder] = useUrlDetails(detailRecords, 'orderId');

  const handleOrderNavigate = async (targetOrderId) => {
    const normalizedTargetId = String(targetOrderId || '');
    const existingOrder = detailRecords.find((candidate) => {
      const candidateData = candidate?.data || candidate;
      const candidateId = candidateData?.OrderId || candidateData?.orderId || candidateData?.order_id;
      return String(candidateId || '') === normalizedTargetId;
    });
    const targetOrder = existingOrder || await fetchOrderById(pageType, normalizedTargetId);

    if (!targetOrder) {
      throw new Error(`Order ${normalizedTargetId} was not found.`);
    }

    if (!existingOrder) {
      setNavigatedOrder(targetOrder);
    }
    setSelectedOrder(targetOrder);
  };

  const handleOrderStatusChange = (orderId, nextStatus) => {
    const key = String(orderId || '');
    if (!key) {
      return;
    }
    const normalizedNextStatus = String(nextStatus || '').toLowerCase();
    latestLiveStatus.current.set(key, normalizedNextStatus);

    setOrderUpdates((current) => {
      const sourceOrder = current[key] || visibleSourceOrders.find((order) => {
        const orderData = order.data || order;
        const sourceOrderId = orderData.OrderId || orderData.orderId || orderData.order_id;
        return String(sourceOrderId || '') === key;
      }) || (String(linkedOrderId || '') === key ? linkedOrder : null);

      if (!sourceOrder) {
        return current;
      }

      if (sourceOrder.data) {
        return {
          ...current,
          [key]: {
            ...sourceOrder,
            data: {
              ...sourceOrder.data,
              Status: nextStatus
            }
          }
        };
      }

      return {
        ...current,
        [key]: {
          ...sourceOrder,
          Status: nextStatus
        }
      };
    });
    highlightRow(key, normalizedNextStatus);

    nextCheckAt.current.delete(key);

    if (statusFilter !== 'all' && normalizedNextStatus !== statusFilter) {
      onTotalChangeRef.current?.(-1);
    }

    fetchOrderUntilStatus(pageType, key, normalizedNextStatus)
      .then((freshOrder) => {
        if (!freshOrder || latestLiveStatus.current.get(key) !== normalizedNextStatus) return;
        setOrderUpdates((current) => ({
          ...current,
          [key]: freshOrder
        }));
        if (statusFilter !== 'all') scheduleFallbackRefresh();
      })
      .catch((error) => {
        console.error(`Failed to confirm updated order ${key}:`, error);
      });
  };

  if (orders.length === 0 && !linkedOrder) {
    return (
      <div className="orders-empty">
        <p>No data</p>
      </div>
    );
  }

  const formatDate = (timestamp) => {
    if (!timestamp) {
      return '-';
    }

    try {
      const date = new Date(normalizeTimestamp(timestamp));
      return Number.isNaN(date.getTime()) ? String(timestamp) : date;
    } catch {
      return String(timestamp);
    }
  };

  const formatDateCell = (timestamp) => {
    const parsed = formatDate(timestamp);
    if (parsed === '-' || typeof parsed === 'string') {
      return parsed;
    }

    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    const hours = String(parsed.getHours()).padStart(2, '0');
    const minutes = String(parsed.getMinutes()).padStart(2, '0');
    const seconds = String(parsed.getSeconds()).padStart(2, '0');

    return (
      <div className="orders-date-cell">
        <span>{day}.{month}.{year}</span>
        <span>{hours}:{minutes}:{seconds}</span>
      </div>
    );
  };

  const formatCountdown = (created, timeout) => {
    const createdDate = formatDate(created);
    const timeoutSeconds = timeout === null || timeout === undefined || timeout === ''
      ? NaN : Number(timeout);

    if (!(createdDate instanceof Date) || !Number.isFinite(timeoutSeconds) || timeoutSeconds < 0) {
      return null;
    }

    const secondsLeft = Math.max(0, Math.ceil((createdDate.getTime() + timeoutSeconds * 1000 - now) / 1000));
    const hours = Math.floor(secondsLeft / 3600);
    const minutes = Math.floor((secondsLeft % 3600) / 60);
    const seconds = secondsLeft % 60;
    const value = hours > 0
      ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      : `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    return (
      <div className={`orders-countdown${secondsLeft === 0 ? ' is-expired' : ''}`}>
        <span>{value}</span>
      </div>
    );
  };

  const renderStatus = (rawStatus) => {
    const value = String(rawStatus || 'unknown');
    const normalized = value.toLowerCase();

    return (
      <span className={`status-pill status-${normalized}`} data-status={value}>
        {value}
      </span>
    );
  };

  const displayColumns = [
    {
      key: 'Created',
      label: 'Created',
      getValue: (order) => {
        const orderData = order.data || order;
        const created = orderData.created || orderData.Created || orderData.create_time
          || order.create_time || null;
        const status = String(orderData.Status || orderData.status || '').toLowerCase();
        const timeout = orderData.timeout ?? orderData.Timeout ?? orderData.TIMEOUT
          ?? order.timeout ?? order.Timeout ?? order.TIMEOUT;

        if (status === 'pending') {
          const countdown = formatCountdown(created, timeout);
          if (countdown) {
            return countdown;
          }
        }

        return formatDateCell(created);
      }
    },
    ...(showTraderName ? [{
      key: 'TraderName',
      label: 'Trader',
      getValue: (order) => {
        const orderData = order.data || order;
        const traderId = orderData.traderId || orderData.TraderId || orderData.TRADER_ID;
        return tradersLoading ? 'Loading...' : getTraderNameById(traderId);
      }
    }] : []),
    ...(showTraderName ? [{
      key: 'Gateway',
      label: 'Gateway',
      getValue: (order) => {
        const orderData = order.data || order;
        return orderData.Gateway || orderData.gateway || '-';
      }
    }] : []),
    {
      key: 'OrderId',
      label: 'Order ID',
      getValue: (order) => {
        const orderData = order.data || order;
        const orderId = orderData.OrderId || orderData.orderId || orderData.order_id || '-';
        if (orderId === '-') {
          return '-';
        }

        const isCopied = copiedId === orderId;

        return (
          <div className="order-id-cell">
            <span className="order-id-text">{orderId}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(orderId, orderId);
              }}
              className="copy-button"
              title={isCopied ? 'Copied' : 'Copy to clipboard'}
            >
              {isCopied ? 'Done' : 'Copy'}
            </button>
          </div>
        );
      }
    },
    {
      key: 'Duplicate',
      label: '',
      getValue: (order) => {
        const orderData = order.data || order;
        const rawOrderData = order.rawCorezoidData?.data || order.rawCorezoidData || {};
        const duplicatedFrom = orderData.duplicatedFrom
          ?? orderData.DuplicatedFrom
          ?? orderData.duplicated_from
          ?? rawOrderData.duplicatedFrom
          ?? rawOrderData.DuplicatedFrom
          ?? rawOrderData.duplicated_from;
        const isDuplicated = duplicatedFrom !== null
          && duplicatedFrom !== undefined
          && duplicatedFrom !== '';

        return isDuplicated ? (
          <span
            className="duplicated-order-badge"
            aria-label={`Duplicated from ${duplicatedFrom}`}
            title={`Duplicated from ${duplicatedFrom}`}
          >
            D
          </span>
        ) : null;
      }
    },
    {
      key: 'Amount',
      label: 'Amount',
      getValue: (order) => {
        const orderData = order.data || order;
        const amountFiat = orderData.amountFiat || orderData.AmountFiat || orderData.amount || orderData.Amount;
        const fiatCurrency = orderData.fiatCurrency || orderData.FiatCurrency || orderData.currency || orderData.Currency || '';
        const amountUsdt = orderData.amountUsdt || orderData.AmountUsdt || orderData.usdt || orderData.USDT || '';

        if (amountFiat === null || amountFiat === undefined || amountFiat === '-') {
          return '-';
        }

        const sign = pageType === 'payin' ? '+' : pageType === 'payout' ? '-' : '';

        return (
          <div className="orders-amount-cell">
            <span className={`amount-main amount-${pageType}`}>{sign}{amountFiat} {fiatCurrency}</span>
            {amountUsdt && <span className="amount-meta">{amountUsdt} USDT</span>}
          </div>
        );
      }
    },
    {
      key: 'Method',
      label: 'Method',
      getValue: (order) => {
        const orderData = order.data || order;
        return orderData.Method || orderData.method || orderData.METHOD || '-';
      }
    },
    {
      key: 'Status',
      label: 'Status',
      getValue: (order) => {
        const orderData = order.data || order;
        return renderStatus(orderData.Status || orderData.status || '-');
      }
    }
  ];

  const totalItems = pagination?.total ?? orders.length;

  return (
    <>
      <div className={`orders-table-container${isTableHeaderStuck ? ' is-header-stuck' : ''}`}>
        <div className="orders-sticky-surface" aria-hidden="true" />
        <div className="orders-header">
          <h3>{totalItems}</h3>
        </div>

        <div ref={tableWrapperRef} className="table-wrapper">
          <table className={`orders-table ${showTraderName ? 'admin-mobile-orders' : 'compact-mobile-orders'}`}>
            <thead>
              <tr>
                {displayColumns.map((column) => (
                  <th
                    key={column.key}
                    className={`column-${column.key.toLowerCase()}`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((order, index) => {
                const orderData = order.data || order;
                const uniqueKey = orderData.OrderId || orderData.orderId || order.ref || index;
                const rowHighlight = rowHighlights[String(uniqueKey)];
                const rowClassName = rowHighlight
                  ? `order-row live-row-${rowHighlight.status}-${rowHighlight.variant}`
                  : 'order-row';

                return (
                  <tr
                    key={uniqueKey}
                    className={rowClassName}
                    onClick={() => setSelectedOrder(order)}
                  >
                    {displayColumns.map((column) => (
                      <td
                        key={`${uniqueKey}-${column.key}`}
                        className={`column-${column.key.toLowerCase()}`}
                        data-label={column.label}
                      >
                        {column.getValue(order)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {pagination && onPageChange && pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            itemsPerPage={pagination.limit}
            totalItems={pagination.total}
          />
        )}
      </div>

      {selectedOrder && (
        <OrderDetailsPanel
          order={selectedOrder}
          pageType={pageType}
          onClose={() => setSelectedOrder(null)}
          onStatusChange={handleOrderStatusChange}
          onOrderNavigate={handleOrderNavigate}
        />
      )}
    </>
  );
};

export default OrdersTable;
